import { z } from 'zod';
import { IdSchema, InstantSchema } from '../domain/entities/common.ts';
import { money, type Money } from '../domain/money.ts';

/**
 * What the assistant cost, per company (SCRUM-223).
 *
 * Buildmate holds one API key and relays every call, so the usage a
 * customer is billed for is **our** count, not the provider's. That makes
 * this file the thing an invoice is computed from, and it has to be right
 * in a way that an estimate does not.
 *
 * Three decisions follow from that.
 *
 * **Tokens are recorded by type, never as one number.** Input, output and
 * cache reads are priced differently, so a single total cannot be turned
 * into money afterwards. A record that lost the breakdown would be
 * unbillable the moment a rate changed.
 *
 * **The model is recorded on every row.** Rates are per model, and a
 * company that switches model mid-month has two rates in one invoice.
 *
 * **Nothing is priced by this file.** `RATE_CARD` ships empty. The
 * provider's prices are not ours to hardcode — they change, they differ
 * per account, and a figure invented here would be invoiced to somebody.
 * `costOf` returns `null` for a model with no rate, and the screen says so
 * rather than drawing a confident zero. The same rule as the equipment
 * maintenance guidance, for the same reason.
 */
export const AssistantUsageSchema = z.object({
  id: IdSchema,
  companyId: IdSchema,
  /** Who asked. A company reading its own bill wants to know. */
  uid: IdSchema,
  at: InstantSchema,
  model: z.string().min(1).max(100),
  inputTokens: z.number().int().min(0),
  outputTokens: z.number().int().min(0),
  cacheReadTokens: z.number().int().min(0).default(0),
  cacheWriteTokens: z.number().int().min(0).default(0),
  /** How many tool round trips this answer took. Useful when a bill jumps. */
  steps: z.number().int().min(0).default(1),
});
export type AssistantUsage = z.infer<typeof AssistantUsageSchema>;

export interface UsageTotals {
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
  /** Every token, for an allowance that does not care about the breakdown. */
  readonly totalTokens: number;
  readonly calls: number;
}

export const NO_USAGE: UsageTotals = {
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  totalTokens: 0,
  calls: 0,
};

export function usageTotals(records: readonly AssistantUsage[]): UsageTotals {
  return records.reduce<UsageTotals>((sum, record) => {
    const input = sum.inputTokens + record.inputTokens;
    const output = sum.outputTokens + record.outputTokens;
    const cacheRead = sum.cacheReadTokens + (record.cacheReadTokens ?? 0);
    const cacheWrite = sum.cacheWriteTokens + (record.cacheWriteTokens ?? 0);
    return {
      inputTokens: input,
      outputTokens: output,
      cacheReadTokens: cacheRead,
      cacheWriteTokens: cacheWrite,
      totalTokens: input + output + cacheRead + cacheWrite,
      calls: sum.calls + 1,
    };
  }, NO_USAGE);
}

/** Usage within one calendar month, which is the unit a cap is set in. */
export function usageInMonth(records: readonly AssistantUsage[], month: string): AssistantUsage[] {
  // `YYYY-MM`, compared as a prefix: instants are ISO UTC strings, so this
  // needs no parsing and no time zone to get wrong.
  return records.filter((record) => record.at.startsWith(month));
}

/**
 * What one company may spend in a month, in tokens.
 *
 * In tokens rather than money because that is what we can count at the
 * moment of the call. Converting to money needs a rate card, and a cap
 * that could only be enforced after pricing would be a cap enforced too
 * late.
 */
export interface AssistantAllowance {
  /** Zero means the assistant is off for this company. */
  readonly monthlyTokens: number;
}

export const ASSISTANT_OFF: AssistantAllowance = { monthlyTokens: 0 };

export interface AllowanceState {
  readonly used: number;
  readonly allowance: number;
  readonly remaining: number;
  /** Past the cap. The next request is refused. */
  readonly exhausted: boolean;
  /** Close enough that somebody should be told before it stops. */
  readonly nearlyGone: boolean;
}

/** Warn here, because a cap that stops without notice reads as a fault. */
export const NEARLY_GONE_AT = 0.8;

export function allowanceState(used: number, allowance: AssistantAllowance): AllowanceState {
  const cap = allowance.monthlyTokens;
  const remaining = Math.max(0, cap - used);
  return {
    used,
    allowance: cap,
    remaining,
    exhausted: used >= cap,
    nearlyGone: cap > 0 && used >= cap * NEARLY_GONE_AT,
  };
}

/**
 * Prices, per million tokens, in whole minor units — **set by the
 * maintainer, never by this file**.
 *
 * Empty on purpose. See the note at the top: a price invented here would
 * be invoiced to somebody, and provider pricing is not ours to guess at.
 * Fill this in from the account's own rate sheet.
 */
export interface ModelRates {
  readonly inputPerMillion: number;
  readonly outputPerMillion: number;
  readonly cacheReadPerMillion: number;
  readonly cacheWritePerMillion: number;
  readonly currency: string;
}

export type RateCard = Readonly<Record<string, ModelRates>>;

export const RATE_CARD: RateCard = {};

/**
 * What a company's usage comes to, or `null` when we cannot say.
 *
 * `null` rather than zero for a model with no rate on file. A bill of
 * $0.00 and "we do not know what this cost" are different statements, and
 * only one of them should ever reach an invoice.
 */
export function costOf(
  records: readonly AssistantUsage[],
  rates: RateCard = RATE_CARD,
): Money | null {
  if (records.length === 0) return money(0);

  const models = new Set(records.map((record) => record.model));
  for (const model of models) if (!rates[model]) return null;

  let total = 0;
  let currency = 'USD';
  for (const record of records) {
    const rate = rates[record.model]!;
    currency = rate.currency;
    // Rounded once at the end of each record rather than per token, and
    // rounded **up**: a fraction of a cent absorbed in our favour on every
    // call would be a slow overcharge nobody could explain.
    total += Math.ceil(
      (record.inputTokens * rate.inputPerMillion +
        record.outputTokens * rate.outputPerMillion +
        (record.cacheReadTokens ?? 0) * rate.cacheReadPerMillion +
        (record.cacheWriteTokens ?? 0) * rate.cacheWritePerMillion) /
        1_000_000,
    );
  }
  return money(total, currency);
}
